import { Path, LocationDescriptor } from 'history'

import {
  api,
  identityApi,
  storeHistory,
  devLogger
} from "../internal";

// The OIDC teardown (forget the in-memory user + clear stale sign-in state) is
// INJECTED by the auth manager adapter rather than imported here. A static import
// of userManager would drag it into evaluation while this module (part of
// `./helpers`) is loading — before `buildOidcAcrValues`/the store exist — which
// crashes the UniAuthManager construction. See registerOidcTeardown below.
let _oidcTeardown: (() => void) | undefined;
export const registerOidcTeardown = (fn: () => void): void => { _oidcTeardown = fn; };

export const getCurrentBaseDomain = () => {
  // split by any query params and hashes 
  // only return the first leading part (the domain)
  return window.location.href.split(/[\?\#]/)?.[0];
}

// type IHistoryPushArgs = Path | LocationDescriptor<any>;
// export const portalRedirect = (config: IHistoryPushArgs) => {
//   devLogger(`portal redirect to: ${JSON.stringify(config)}`);
//   storeHistory.push(config);
// }

export const clearAllAuthValues = () => {
  api.clearAuthValues();
  identityApi.clearAuthValues();
  api.cacheImpl.removeAllWithPrefix('oidc.');
  // Also forget the in-memory OIDC user and clear stale sign-in state. Clearing
  // the `oidc.` cache keys above only wipes storage; the UserManager can still
  // hold an in-memory user, and a stale PKCE `state` can seed a "No matching
  // state" login loop. The manager guards its own onRemoveUser side effects
  // while tearing down (see RootContainer), so this won't re-trigger a redirect.
  try {
    _oidcTeardown?.();
  } catch (e) {
    /* teardown not yet registered at call time */
  }
}

export const cleanOidcUrlParams = () => {
  // clear any lingering oidc query param values (recommended by react-oidc-context)
  if (/code=.*scope=.*state=.*/.test(window.location.search)) {
    window.history.replaceState({}, document.title, `/${window.location.hash}`);
  }
}

export const isLocationRootUrl = () => {
  return window.location.pathname === '/' || window.location.pathname === '/#/';
}

export const isInviteAcceptUrl = (url: string) => {
  return /\#\/invites/.test(url);
}

export const isLocationInviteAcceptUrl = () => {
  return isInviteAcceptUrl(window.location.hash);
}

export const isLocationVerifyUrl = () => {
  return /\#\/verify/.test(window.location.hash);
}

// The pending admin invite is stored separately from `continueTo` so it survives the
// login -> register -> email verification round trip (which may span tabs, since the
// cache falls back to localStorage). It is cleared once an authenticated user has been
// delivered to the accept page, so it can't hijack later token refreshes.
const pendingAdminInviteCacheKey = 'pendingAdminInvitePath';

export const setPendingAdminInvitePath = (acceptPath: string) => {
  api.cacheImpl.set(pendingAdminInviteCacheKey, acceptPath);
}

export const getPendingAdminInvitePath = (): string | undefined => {
  const acceptPath = api.cacheImpl.get(pendingAdminInviteCacheKey);
  return acceptPath && acceptPath !== 'null' ? acceptPath : undefined;
}

export const clearPendingAdminInvitePath = () => {
  api.cacheImpl.remove(pendingAdminInviteCacheKey);
}

export const isLocationAcceptOrVerifyUrl = () => {
  return isLocationInviteAcceptUrl() || isLocationVerifyUrl();
}